// The two Houses, side by side — the arithmetic, and the biggest thing on the
// page.
//
// THREE rules hold this component's shape, and each of them prevents a specific
// wrong declaration:
//
//  • **No "Total" column, no aggregate row, and no page-level verdict band.** A
//    motion counted by Houses is a CONJUNCTION (SO 8.6, motion.ts:187): a
//    majority in EACH House, never a sum. 25-5 in one House does not make up for
//    10-20 in the other, so there is no number that is the sum of the two and no
//    cell on this screen may look like one. If you are here to add a totals
//    column: the number you would put in it does not exist in the Constitution.
//  • **The two Houses stay SIDE BY SIDE, as a real <table>, at every width** —
//    it does not stack into per-House cards on a phone. Two Houses stacked read
//    as a sequence, and the second reads as subordinate to the first; side by
//    side the eye reads the conjunction. It is also not wrapped in `.table-wrap`:
//    a conjunction you have to scroll sideways to compare defeats the point of
//    putting the two columns together. If it overflows at 320px the fix is
//    narrower row labels, not a scroll box.
//  • **Every number is `--color-ink`.** A tally is never tinted by its outcome.
//    Only the Result row carries a verdict colour, and only through the shared
//    `.pill` roles.
//
// The Bishop is NOT a column, and after registrar ruling 2026-08-16 he is not a
// line beneath the table either. He sits in neither House and there is no "House
// of Bishops" (ruling 2026-08-15), so his paper is either INSIDE the clergy
// figures — on a `present-and-voting` base, where the base is the votes
// themselves and he enters numerator and denominator in the same breath — or
// excluded from every tally on this screen and reported as `bishop-vote-excluded`
// in the issue list. Nothing here is his own tally: one identified person's
// tally is an attribution (SO 15.9), and engine version 2 dissolved it rather
// than suppressing it. There is no `result.bishop` to render.
//
// **NOTHING ON THIS TABLE IS GATED BY WHO IS READING, because everyone who may
// open this page may see every figure on it** (registrar ruling, 2026-08-16: the
// sheet is Admin and Scrutineers, and nobody else at any distance). A tally with
// holes in it reports nothing to anybody. The audience question is asked once,
// about the whole page, and it is not answered by this file — see the banner at
// the top of ResultPage.tsx.

import type { JSX } from 'react';
import type { MajorityBase, MotionItemResult } from '@cca/decides-count';
import { SCOPE_COLUMN_LABEL } from './view';

/** What the denominator IS, in the reader's words, per base. Keyed on the
 *  engine's union so a fourth base reds this file rather than leaving a row
 *  labelled with the wrong noun — which is the whole difficulty of the count
 *  (motion.ts:4: "WHICH NUMBER IS THE DENOMINATOR"). */
const BASE_LABEL: Record<MajorityBase, string> = {
  'present-and-voting': 'Counted against (ayes and noes)',
  'summoned-per-house': 'Members summoned',
  'all-synod-members': 'Members of Synod',
};

interface HouseTallyProps {
  result: MotionItemResult;
}

export function HouseTally({ result }: HouseTallyProps): JSX.Element {
  const { scopes } = result;

  // THREE-STATE FIELD, rule 1 of 3: `notVoting === null` means "not a meaningful
  // subtraction here" (a present-and-voting base IS the votes), not "nobody".
  // The row is therefore absent from the DOM entirely rather than showing 0 — a
  // zero in a turnout column, read by eye, is a false statement about who stayed
  // away. Rendered only when EVERY scope has a number, because a blank cell in
  // one column of a row that exists in the other is the same lie with a smaller
  // font.
  //
  // `scopes.length > 0` first, and it is not padding: `[].every(…)` is `true`,
  // so on an empty scope list the bare `every` would report "show the row" —
  // a guard that passes by finding nothing to check.
  //
  // AND THE SAME ROW CARRIES A SECOND FALSEHOOD when a scope is withheld: with
  // more papers than people, `base - cast` is NEGATIVE, and "Did not vote: −1"
  // is not a small number of people, it is not a number of people at all. That
  // cell is the difference between the two disagreeing figures, wearing the
  // wrong label and the wrong sign. Registrar, 2026-08-16: "what is wrong is the
  // label, not the number" — so the row STAYS, the figure survives one card
  // higher under a label that is true, and the cell here points at that card
  // exactly as the Result cell below it does.
  const showNotVoting = scopes.length > 0 && scopes.every((scope) => scope.notVoting !== null);

  return (
    <table className="table result-table">
      <caption className="result-table-caption">
        {result.byHouses
          ? 'What was counted in each House, and what the motion needed to carry.'
          : 'What was counted, and what the motion needed to carry.'}
      </caption>
      <thead>
        <tr>
          {/* The corner cell is a real <th scope="col"> with a label for a screen
              reader: an empty corner leaves the row headers below it announced
              under nothing. */}
          <th scope="col">
            <span className="ballot-sr">What was counted</span>
          </th>
          {/* SHORT headings — "Clergy", not "House of Clergy". The long form set
              this column's min-content width and broke SC 1.4.10 Reflow at
              390px (measured, not reasoned about: scrollWidth 552 against a
              clientWidth of 390, with "House of Laity" cut off at the edge). The
              caption above says "in each House" and the verdict below names both
              in full, so the words are not lost — only the width is. */}
          {scopes.map((scope) => (
            <th scope="col" className="num" key={scope.scope}>
              {SCOPE_COLUMN_LABEL[scope.scope]}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {/* THE PAPER'S OWN THREE WORDS — "Aye", "No", "Abstain", exactly as
            VOTE_OPTIONS labels them in ../paper.ts. A scrutineer reading this
            sheet beside a ballot paper should meet one vocabulary, not two, and
            it is how a division sheet has always read.
            It is also the first lever in this screen's overflow order, and it
            was needed: "Abstained" is 9 characters, and at 320px the row-label
            column is ~100px, so the longest word in it decides whether anything
            gets broken in half. "Abstain" is 7. */}
        <tr>
          <th scope="row">Aye</th>
          {scopes.map((scope) => (
            <td className="num" key={scope.scope}>
              {scope.ayes}
            </td>
          ))}
        </tr>
        <tr>
          <th scope="row">No</th>
          {scopes.map((scope) => (
            <td className="num" key={scope.scope}>
              {scope.noes}
            </td>
          ))}
        </tr>
        <tr>
          {/* Abstain is a RECORDED choice, not a non-vote, and not a No. It has
              its own row for the same reason it is a listed option at equal
              weight on the paper. */}
          <th scope="row">Abstain</th>
          {scopes.map((scope) => (
            <td className="num" key={scope.scope}>
              {scope.abstentions}
            </td>
          ))}
        </tr>
        <tr>
          <th scope="row">Blank papers</th>
          {scopes.map((scope) => (
            <td className="num" key={scope.scope}>
              {scope.blanks}
            </td>
          ))}
        </tr>
        <tr>
          <th scope="row">Papers counted</th>
          {scopes.map((scope) => (
            <td className="num" key={scope.scope}>
              {scope.cast}
            </td>
          ))}
        </tr>
        <tr>
          {/* THE number to check a declaration against. */}
          <th scope="row">{BASE_LABEL[result.base]}</th>
          {scopes.map((scope) => (
            <td className="num" key={scope.scope}>
              {scope.base}
            </td>
          ))}
        </tr>
        {showNotVoting ? (
          <tr>
            <th scope="row">Did not vote</th>
            {/* Gated on the ENGINE's own withheld flag (`carried === null`),
                never on `notVoting < 0`. Re-deriving the finding from the sign
                would make this screen a second opinion about whether the count
                is sound, and it would also point at a card that is not on the
                page: the card renders for withheld scopes and for nothing else,
                so any other predicate can produce a cross-reference to nowhere.
                `.result-cell`, not `.num`: this cell holds a phrase, and `.num`
                carries both the figure size and the one `nowrap` on the screen. */}
            {scopes.map((scope) =>
              scope.carried === null ? (
                <td className="result-cell" key={scope.scope}>
                  <span className="result-see-note">See the note above</span>
                </td>
              ) : (
                <td className="num" key={scope.scope}>
                  {scope.notVoting}
                </td>
              ),
            )}
          </tr>
        ) : null}
        <tr>
          <th scope="row">Needed to carry</th>
          {scopes.map((scope) => (
            <td className="num" key={scope.scope}>
              {scope.required}
            </td>
          ))}
        </tr>
        <tr>
          {/* Deliberately NOT `.num`: that class carries this screen's figure
              size, and the result cell holds a word, not a number. The two are
              styled apart so the tally stays the largest type on the page and
              the verdict word does not compete with it. */}
          <th scope="row">Result</th>
          {scopes.map((scope) => (
            <td className="result-cell" key={scope.scope}>
              <ScopeResult carried={scope.carried} />
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

/**
 * THREE-STATE FIELD, rule 2 of 3: on `carried === null` **no pill element exists
 * in the DOM**. Not a grey pill, not an empty one — none. The point is
 * structural: "no decision" cannot be folded into "lost" by a later edit to a
 * colour or a label, because there is no element there to re-colour. Anyone
 * doing it has to add a component that is deliberately not here, and will read
 * this comment on the way.
 *
 * The withheld cell points at the card above rather than restating it: the whole
 * explanation is in one place, in words, with the two numbers that disagree.
 */
function ScopeResult({ carried }: { carried: boolean | null }): JSX.Element {
  if (carried === null) return <span className="result-see-note">See the note above</span>;
  if (carried) return <span className="pill pill-success result-pill">Carried</span>;
  return <span className="pill pill-danger result-pill">Not carried</span>;
}
